import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Factory, Home, Store, Hotel } from "lucide-react";
import { useSite, tx } from "@/lib/lang";
import { ui } from "@/lib/uiStrings";
import { Reveal, SectionLabel } from "./Reveal";

const ICONS = { sgr: Building2, industrie: Factory, condomini: Home, pmi: Store, hospitality: Hotel };

const CHIPS = {
  sgr: { it: ["Multi-Asset", "Gare Portfolio", "Reporting"], en: ["Multi-Asset", "Portfolio Tenders", "Reporting"] },
  industrie: { it: ["Trading Desk", "Fix/Flex/Mix", "Audit Tecnici"], en: ["Trading Desk", "Fix/Flex/Mix", "Technical Audits"] },
  condomini: { it: ["Luce & Gas", "Fotovoltaico", "Pratiche GSE"], en: ["Power & Gas", "Solar PV", "GSE Applications"] },
  pmi: { it: ["Credit Check", "Supplier Fit", "Contratti Su Misura"], en: ["Credit Check", "Supplier Fit", "Tailored Contracts"] },
  hospitality: { it: ["Multi-sito", "Stagionalità", "Nuove Aperture"], en: ["Multi-site", "Seasonality", "New Openings"] },
};

export default function Target() {
  const { content, lang } = useSite();
  const segments = content.target || [];

  return (
    <section id="target" data-testid="target-section" className="py-16 lg:py-24 bg-surface/30 border-y border-ice/5">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal className="max-w-2xl">
          <SectionLabel>{ui[lang].labels.target}</SectionLabel>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-ice">
            {tx(content.sezioni?.target?.heading, lang) || (lang === "it" ? "Ogni interlocutore, il suo linguaggio." : "Every stakeholder, their own language.")}
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
          {segments.map((s, i) => {
            const Icon = ICONS[s.id] || Building2;
            const chips = CHIPS[s.id]?.[lang] || CHIPS[s.id]?.it || [];
            const span = i < 2 ? "lg:col-span-6" : "lg:col-span-4";
            return (
              <Reveal key={s.id} delay={i * 0.06} className={`h-full ${span}`}>
                <Link
                  to={`/clienti/${s.id}`}
                  data-testid={`target-card-${s.id}`}
                  className="group relative flex h-full flex-col overflow-hidden bg-surface border border-ice/10 rounded-2xl shadow-[0_4px_20px_rgba(14,27,51,0.03)] hover:shadow-[0_12px_36px_rgba(58,91,217,0.12)] hover:border-royal/50 transition-all duration-300"
                >
                  <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-royal via-azure to-royal opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="p-7 lg:p-8 flex flex-col flex-1">
                    <div className="flex items-start justify-between">
                      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-royal/10 border border-royal/20 text-royal group-hover:bg-royal group-hover:text-white transition-all duration-300">
                        <Icon size={22} />
                      </span>
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-onyx border border-ice/10 text-muted-foreground group-hover:bg-royal group-hover:border-royal group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300">
                        <ArrowUpRight size={15} />
                      </span>
                    </div>
                    <p className="mt-6 font-mono text-xs font-semibold tracking-wider text-royal">
                      {tx(s.label, lang).toUpperCase()}
                    </p>
                    <h3 className="mt-2 font-display text-lg sm:text-xl font-semibold tracking-tight text-ice leading-snug">
                      {tx(s.headline, lang)}
                    </h3>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {chips.map((c) => (
                        <span key={c} className="text-[11px] font-medium text-ice/70 border border-ice/10 rounded-full px-2.5 py-1 bg-onyx/60">
                          {c}
                        </span>
                      ))}
                    </div>
                    <span className="mt-auto pt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-royal group-hover:text-azure transition-colors">
                      {lang === "it" ? "Scopri di più" : "Learn more"}
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
